import { Controller } from "@hotwired/stimulus";

// Renders a live preview of the volume-discount ladder in the editor,
// e.g. "1 for €100 each · 2 for €90 each · 3 for €85 each · 4 or more for €80 each".
// Sits on the whole ticket-type form so it can watch the price field on the
// Basics tab as well as the discount rows under Additional settings.
export default class extends Controller {
  static targets = ["basePrice", "field", "output"];
  static values = { currency: String };

  connect() {
    this.render();
  }

  render() {
    if (!this.hasOutputTarget) return;

    if (this.hasFieldTarget && !this.fieldTarget.checked) {
      this.outputTarget.innerHTML = "";
      return;
    }

    const tiers = this.#tiers();
    if (tiers.length < 2) {
      this.outputTarget.innerHTML = "";
      return;
    }

    this.outputTarget.innerHTML = tiers
      .map((tier, index) => this.#line(tier, tiers[index + 1]))
      .join(" · ");
  }

  #tiers() {
    const tiers = [];

    const base = this.hasBasePriceTarget
      ? parseFloat(this.basePriceTarget.value)
      : NaN;
    if (!Number.isNaN(base)) tiers.push({ quantity: 1, price: base });

    this.element.querySelectorAll(".js-reducing-price-row").forEach((row) => {
      if (row.querySelector('input[name*="_destroy"]')?.value === "1") return;
      if (row.hidden || row.style.display === "none") return;

      const quantity = parseInt(
        row.querySelector(".js-reducing-min-quantity")?.value,
        10,
      );
      const price = parseFloat(row.querySelector(".js-reducing-price")?.value);
      if (Number.isNaN(quantity) || Number.isNaN(price)) return;

      tiers.push({ quantity, price });
    });

    return tiers.sort((a, b) => a.quantity - b.quantity);
  }

  #line(tier, next) {
    let range;
    if (!next) range = `${tier.quantity} or more`;
    else if (next.quantity === tier.quantity + 1) range = `${tier.quantity}`;
    else range = `${tier.quantity}–${next.quantity - 1}`;

    return `${range} for ${this.#money(tier.price)} each`;
  }

  #money(amount) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: this.currencyValue,
      }).format(amount);
    } catch {
      return amount.toString();
    }
  }
}
